/* ============================================================
   providerProductEditView — 0e895f9a pure form model for the
   provider product edit page (a1a3b8f4 part 2): draft build from
   a loaded listing, submit normalization, submit guard and media
   list helpers. Pure module — unit-tested in
   tests/providerProductEditView.test.ts.
   ============================================================ */

import type { ProductDetail, ProductDraft, ProductMediaItem } from '../types/product';

/** Matches backend CatalogService.MaxMediaPerProduct (ME-3). */
export const MAX_MEDIA = 12;

/** New-product draft (page root state for create mode). */
export const EMPTY_FORM: ProductDraft = {
  title: '',
  description: '',
  imageUrl: '',
  media: [],
  materialId: '',
  color: '',
  priceRub: 0,
  compareAtPriceRub: null,
  stockCount: null,
  minOrderQuantity: 1,
  maxOrderQuantity: null,
  productionDays: 3,
  publish: true,
  productCategoryId: null,
  options: [],
};

/** Map a loaded listing into an editable draft (options full-replace shape). */
export function buildEditForm(p: ProductDetail): ProductDraft {
  return {
    title: p.title,
    description: p.description ?? '',
    imageUrl: p.imageUrl ?? '',
    media: p.media ?? [],
    materialId: p.materialId ?? '',
    color: p.color,
    priceRub: p.priceRub,
    compareAtPriceRub: p.compareAtPriceRub,
    stockCount: p.stockLeft,
    minOrderQuantity: p.minOrderQuantity,
    maxOrderQuantity: p.maxOrderQuantity,
    productionDays: p.productionDays,
    publish: p.isPublished,
    productCategoryId: p.categoryId,
    options: (p.options ?? []).map((o) => ({
      name: o.name,
      type: o.type,
      priceMode: o.priceMode,
      priceRub: o.priceRub,
      enabled: o.enabled,
      values: (o.values ?? []).map((v) => ({ label: v.label, priceRub: v.priceRub })),
    })),
  };
}

/** Normalize the draft into the wire payload (trims, numeric coercion,
 *  blank-optional → null, unnamed options/values dropped, values only on
 *  Choice type — same contract the inline panel form used). */
export function normalizeProductDraft(form: ProductDraft): ProductDraft {
  return {
    ...form,
    title: form.title.trim(),
    description: form.description?.trim() || null,
    imageUrl: form.imageUrl?.trim() || null,
    materialId: form.materialId || null,
    color: form.color?.trim() || null,
    compareAtPriceRub: form.compareAtPriceRub || null,
    stockCount:
      form.stockCount === null || (form.stockCount as unknown as string) === ''
        ? null
        : Number(form.stockCount),
    maxOrderQuantity:
      form.maxOrderQuantity === null || (form.maxOrderQuantity as unknown as string) === ''
        ? null
        : Number(form.maxOrderQuantity),
    minOrderQuantity: Number(form.minOrderQuantity) || 1,
    priceRub: Number(form.priceRub),
    productionDays: Number(form.productionDays) || 3,
    options: (form.options ?? [])
      .filter((o) => o.name.trim().length > 0)
      .map((o) => ({
        name: o.name.trim(),
        type: o.type,
        priceMode: o.priceMode,
        priceRub: Number(o.priceRub) || 0,
        enabled: o.enabled,
        values:
          o.type === 1
            ? (o.values ?? [])
                .filter((v) => v.label.trim().length > 0)
                .map((v) => ({ label: v.label.trim(), priceRub: Number(v.priceRub) || 0 }))
            : null,
      })),
  };
}

/** Submit guard: title present + positive base price (panel parity). */
export function canSubmitProduct(form: ProductDraft): boolean {
  return form.title.trim().length > 0 && Number(form.priceRub) > 0;
}

/** Room check for a gallery batch upload; error text or null when it fits. */
export function mediaUploadError(currentCount: number, adding: number, max = MAX_MEDIA): string | null {
  if (adding <= 0) return null;
  if (currentCount + adding > max) return `Максимум ${max} файлов (фото + видео).`;
  return null;
}

/** Immutable remove of one gallery item by index. */
export function removeMediaAt(media: ProductMediaItem[], index: number): ProductMediaItem[] {
  return media.filter((_, i) => i !== index);
}

/** Immutable swap of item[index] with its neighbour in delta direction
 *  (-1 left / +1 right); out-of-range moves are no-ops. */
export function moveMediaItem(media: ProductMediaItem[], index: number, delta: -1 | 1): ProductMediaItem[] {
  const target = index + delta;
  if (index < 0 || index >= media.length || target < 0 || target >= media.length) {
    return media;
  }
  const next = [...media];
  const swapped = next[target];
  next[target] = next[index];
  next[index] = swapped;
  return next;
}
